import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { chunkCandidateProfile } from "./lib/chunker";
import { generateEmbedding, toPgVector, EmbeddingError } from "./lib/embeddings";

/**
 * Wipe the user's existing chunks and rebuild them from the current candidate profile.
 * Embeds each chunk via Lovable AI Gateway and stores in candidate_chunks.
 */
export const reindexCandidateChunks = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;

    const { data: profile, error: profileErr } = await supabase
      .from("candidate_profiles")
      .select(
        "raw_resume_text, chronological_experience, linkedin_data, portfolio_cases, portfolio_data, github_metrics, github_username",
      )
      .eq("user_id", userId)
      .maybeSingle();

    if (profileErr) {
      throw new Response(`Failed to load profile: ${profileErr.message}`, { status: 500 });
    }
    if (!profile) {
      return { chunksCreated: 0, errors: ["No candidate profile yet"] };
    }

    const chunks = chunkCandidateProfile(profile);
    if (chunks.length === 0) {
      return { chunksCreated: 0, errors: ["Profile is empty — add resume / LinkedIn / portfolio first"] };
    }

    // Wipe existing chunks
    const { error: delErr } = await supabase
      .from("candidate_chunks")
      .delete()
      .eq("user_id", userId);
    if (delErr) {
      throw new Response(`Failed to clear chunks: ${delErr.message}`, { status: 500 });
    }

    // Embed + insert in small batches to stay under any rate-limit
    const errors: string[] = [];
    let inserted = 0;
    const BATCH = 5;
    for (let i = 0; i < chunks.length; i += BATCH) {
      const slice = chunks.slice(i, i + BATCH);
      const rows: Array<Record<string, unknown>> = [];
      for (const ch of slice) {
        try {
          const vec = await generateEmbedding(ch.content);
          rows.push({
            user_id: userId,
            source_type: ch.source_type,
            source_ref: ch.source_ref,
            content: ch.content,
            embedding: toPgVector(vec),
          });
        } catch (e) {
          if (e instanceof EmbeddingError && (e.status === 429 || e.status === 402)) {
            throw new Response(e.message, { status: e.status });
          }
          errors.push(`Embed failed for chunk ${i}: ${(e as Error).message}`);
        }
      }
      if (rows.length) {
        const { error: insErr } = await supabase
          .from("candidate_chunks")
          .insert(rows as never);
        if (insErr) errors.push(`Insert batch ${i}: ${insErr.message}`);
        else inserted += rows.length;
      }
    }

    return { chunksCreated: inserted, totalChunks: chunks.length, errors };
  });
